import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Container, Table, Row, Col, Card } from 'react-bootstrap';
import classes from './style.module.css'
import League from './League'
import { connect } from "react-redux"
import { HORSE_TABLE_COLUMNS_STATIC, HOME, AWAY } from '../../constants/Constants'
import { SET_SOCCER_LEAGUES_STATE, SET_SELECTION_STATE, SET_FILTER } from "../../redux/actions"
import { getTodaysMatch } from '../../service/backend-api-service';
import Spinner from "../UI/Spinner"
import "react-sliding-pane/dist/react-sliding-pane.css"
import sortIconImage from "../../assets/images/sort_both.png"

// Computed once at module level — never changes
const staticColumns = [];
for (let value in HORSE_TABLE_COLUMNS_STATIC) staticColumns.push(HORSE_TABLE_COLUMNS_STATIC[value]);

const getAverageValue = (a, b) => {
    if (a !== null && b !== null && a !== "-" && b !== "-") {
        return ((a + b) / 2).toFixed(2)
    }
    return "-"
}

const LeagueTable = (props) => {
    const [sortOrder, setSortOrder] = useState(true) // true = ascending
    const [sortColumn, setSortColumn] = useState(null)
    const [loadingState, setLoadingState] = useState(1)
    const [runner, setRunner] = useState({})
    const [oddFilter, setOddFilter] = useState({})
    const [durationFilter, setDurationFilter] = useState({})

    // Stable sort icon handler — setSortColumn functional update avoids stale closure
    const sortIconClickedHandler = useCallback((columnId) => {
        setSortColumn(prev => {
            if (prev === columnId) setSortOrder(o => !o)
            return columnId
        })
    }, [])

    // Table headers only recompute when sort handler changes (i.e. never after mount)
    const tableHeaders = useMemo(() => staticColumns.map(column => {
        let desc = column.description;
        let sortIcon = null
        if (column === HORSE_TABLE_COLUMNS_STATIC.HOME_WIN_PERCENT ||
            column === HORSE_TABLE_COLUMNS_STATIC.DRAW_WIN_PERCENT ||
            column === HORSE_TABLE_COLUMNS_STATIC.AWAY_WIN_PERCENT ||
            column === HORSE_TABLE_COLUMNS_STATIC.COMB_GOALS_SCORED_CONCEDED ||
            column === HORSE_TABLE_COLUMNS_STATIC.COMB_OVER_TWO_POINT_FIVE ||
            column === HORSE_TABLE_COLUMNS_STATIC.COMB_BTTS) {
            sortIcon = <img src={sortIconImage} alt="Sort" onClick={() => sortIconClickedHandler(column.id)} />
        }
        if (column === HORSE_TABLE_COLUMNS_STATIC.SCORE) {
            desc = (
                <div style={{ display: "flex" }}>
                    <div style={{ minWidth: "30px" }}>CS</div>
                    <div style={{ marginLeft: "5px", minWidth: "75px" }}>Status</div>
                    <div style={{ marginLeft: "5px", minWidth: "25px" }}>Time</div>
                    <div style={{ marginLeft: "5px", minWidth: "30px" }}>HT</div>
                </div>
            )
        }
        if (column === HORSE_TABLE_COLUMNS_STATIC.DFS_STAT) {
            desc = (
                <>
                    <div style={{ float: "left", width: "10px" }}>CGS+GCPM</div>
                    <div style={{ float: "left", marginLeft: "5px" }}>CO2.5</div>
                    <div style={{ float: "left", marginLeft: "5px" }}>CBTTS</div>
                </>
            )
        }
        if (column === HORSE_TABLE_COLUMNS_STATIC.ODD_DETAILS) {
            desc = (
                <div style={{ display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>
                    {/* Section 1: same width+margin as OddResultCell section 1 (222px + 0 5px) */}
                    <div style={{ width: "222px", display: "flex", alignItems: "center", margin: "0 5px", flexShrink: 0 }}>
                        <span style={{ minWidth: "22px", flexShrink: 0 }}></span>
                        <span style={{ width: "50px", flexShrink: 0, textAlign: "center" }}>Home</span>
                        <span style={{ width: "50px", flexShrink: 0, textAlign: "center" }}>Draw</span>
                        <span style={{ width: "50px", flexShrink: 0, textAlign: "center" }}>Away</span>
                        <span style={{ marginLeft: "auto", paddingLeft: "4px", flexShrink: 0 }}>Vol</span>
                    </div>
                    {/* Section 2: same width+margin as OddResultCell section 2 (138px + 0 5px) */}
                    <div style={{ width: "138px", display: "flex", alignItems: "center", margin: "0 5px", flexShrink: 0 }}>
                        <span style={{ width: "50px", flexShrink: 0, textAlign: "center" }}>U 2.5</span>
                        <span style={{ width: "50px", flexShrink: 0, textAlign: "center" }}>O 2.5</span>
                        <span style={{ marginLeft: "auto", paddingLeft: "4px", flexShrink: 0 }}>Vol</span>
                    </div>
                    {/* Section 3: same gap+margin+width as OddResultCell section 3 (90px + 0 5px) */}
                    <div style={{ display: "flex", alignItems: "center", gap: "4px", margin: "0 5px", width: "90px", flexShrink: 0 }}>
                        <span>Score</span>
                        <span>Vol</span>
                    </div>
                </div>
            )
        }
        return (
            <th key={column.id} title={column.toolTip || ''}>{desc}{sortIcon}</th>
        );
    }), [sortIconClickedHandler])

    // Fetch on date change — also resets state
    useEffect(() => {
        const fetchData = async () => {
            const date = props.selectionState.selectionDate
            if (date) {
                setLoadingState(0)
                const apiData = await getTodaysMatch(date)
                props.setSoccerLeaguesState(apiData)
                setLoadingState(apiData ? 2 : 1)
            }
        }
        setRunner({});
        setLoadingState(1);
        props.setSoccerLeaguesState(null);
        fetchData();
    }, [props.selectionState.selectionDate]) // eslint-disable-line react-hooks/exhaustive-deps

    // Stable row-click handler
    const clickHandler = useCallback((item) => {
        setRunner(item);
    }, [])

    // Create new objects for filters so useMemo deps update correctly
    const oddChangeHandler = useCallback((eventId, type, odd) => {
        setOddFilter(prev => ({ ...prev, [eventId + type]: odd }))
    }, [])

    const matchStatusChangeHandler = useCallback((eventId, score) => {
        setDurationFilter(prev => ({ ...prev, [eventId]: score }))
    }, [])

    const objectValue = (myObject, property) => {
        return myObject && Object.prototype.hasOwnProperty.call(myObject, property) ? myObject[property] : null;
    }

    // All sorting, stat computation, and filtering happens here — NO Redux dispatch on sort/toggle
    const itemList = useMemo(() => {
        if (!props.soccerLeaguesState) return null;

        const { home, away } = props.selectionState;

        // Mutate calculated columns in-place so item object references stay stable
        // (stable refs let React.memo on League skip re-renders)
        props.soccerLeaguesState.forEach(match => {
            match.combGoalsScoredConc = "-"
            match.combOverTwoPointFive = "-"
            match.combBtts = "-"
            if (match.matchStat && match.matchStat.homeStat !== null && match.matchStat.awayStat !== null) {
                const v1 = getAverageValue(
                    home === HOME ? match.matchStat.homeStat.avgGoalsScoredConc : match.matchStat.homeStat.avgGoalsScoredConcTotal,
                    away === AWAY ? match.matchStat.awayStat.avgGoalsScoredConc : match.matchStat.awayStat.avgGoalsScoredConcTotal
                )
                match.combGoalsScoredConc = !isNaN(v1) ? parseFloat(v1) : "-"

                const v2 = getAverageValue(
                    home === HOME ? match.matchStat.homeStat.overTwoPointFiveGoals : match.matchStat.homeStat.overTwoPointFiveGoalsTotal,
                    away === AWAY ? match.matchStat.awayStat.overTwoPointFiveGoals : match.matchStat.awayStat.overTwoPointFiveGoalsTotal
                )
                match.combOverTwoPointFive = !isNaN(v2) ? parseFloat(v2) : "-"

                const v3 = getAverageValue(
                    home === HOME ? match.matchStat.homeStat.bothTeamsScored : match.matchStat.homeStat.bothTeamsScoredTotal,
                    away === AWAY ? match.matchStat.awayStat.bothTeamsScored : match.matchStat.awayStat.bothTeamsScoredTotal
                )
                match.combBtts = !isNaN(v3) ? parseFloat(v3) : "-"
            }
        })

        const compare = (a, b) => {
            if (a === b) return 0
            if (a === null || a === "-") return 1
            if (b === null || b === "-") return -1
            if (sortOrder) return a < b ? -1 : 1;
            return a < b ? 1 : -1;
        }

        // Sort (on a shallow copy of the array — don't mutate Redux array order)
        const sorted = [...props.soccerLeaguesState].sort((a, b) => {
            if (sortColumn === HORSE_TABLE_COLUMNS_STATIC.HOME_WIN_PERCENT.id)
                return compare(a?.prediction?.homePercent, b?.prediction?.homePercent);
            if (sortColumn === HORSE_TABLE_COLUMNS_STATIC.AWAY_WIN_PERCENT.id)
                return compare(a?.prediction?.awayPercent, b?.prediction?.awayPercent);
            if (sortColumn === HORSE_TABLE_COLUMNS_STATIC.DRAW_WIN_PERCENT.id)
                return compare(a?.prediction?.drawPercent, b?.prediction?.drawPercent);
            if (sortColumn === HORSE_TABLE_COLUMNS_STATIC.COMB_GOALS_SCORED_CONCEDED.id)
                return compare(a?.combGoalsScoredConc, b?.combGoalsScoredConc);
            if (sortColumn === HORSE_TABLE_COLUMNS_STATIC.COMB_OVER_TWO_POINT_FIVE.id)
                return compare(a?.combOverTwoPointFive, b?.combOverTwoPointFive);
            if (sortColumn === HORSE_TABLE_COLUMNS_STATIC.COMB_BTTS.id)
                return compare(a?.combBtts, b?.combBtts)
            return 0;
        })

        // Filter
        const filterCriteria = props.filterCriteria;
        const filtered = sorted.filter(element => {
            if (!filterCriteria || Object.keys(filterCriteria).length === 0) return true;
            if (!filterCriteria.market && !filterCriteria.matchDurationMin > 0 &&
                !filterCriteria.matchDurationMax > 0 &&
                !filterCriteria.oddVolume > 0 &&
                !filterCriteria.underOverVolumne > 0 &&
                !filterCriteria.correctScoreVolumne > 0) {
                return true;
            }

            const currentStatus = durationFilter[element.bfEventId];
            const matchToPick = (currentStatus && currentStatus.status && currentStatus.status.shortName === 'FT') ? 'start' : 'current';

            if (filterCriteria.matchDurationMin && filterCriteria.matchDurationMin > 0 &&
                filterCriteria.matchDurationMax && filterCriteria.matchDurationMax > 0) {
                if (currentStatus && currentStatus.status &&
                    (currentStatus.status.elapsed < filterCriteria.matchDurationMin ||
                        currentStatus.status.elapsed > filterCriteria.matchDurationMax ||
                        currentStatus.status.elapsed === 90)) {
                    return false;
                }
                if (!currentStatus || !currentStatus.status) return false;
            }
            if (filterCriteria.oddVolume && filterCriteria.oddVolume > 0) {
                const odd = objectValue(oddFilter, element.bfEventId + matchToPick);
                const value = objectValue(odd, 'matchOddsVolume');
                if (value !== null && value < filterCriteria.oddVolume) return false;
            }
            if (filterCriteria.underOverVolumne && filterCriteria.underOverVolumne > 0) {
                const odd = objectValue(oddFilter, element.bfEventId + matchToPick);
                const value = objectValue(odd, 'overUnderTwoPFiveVolume');
                if (value !== null && value < filterCriteria.underOverVolumne) return false;
            }
            if (filterCriteria.correctScoreVolumne && filterCriteria.correctScoreVolumne > 0) {
                const odd = objectValue(oddFilter, element.bfEventId + matchToPick);
                const value = objectValue(odd, 'ftCorrectScoreVolume');
                if (value !== null && value < filterCriteria.correctScoreVolumne) return false;
            }
            if (filterCriteria.selectionOdd) {
                const odd = objectValue(oddFilter, element.bfEventId + matchToPick);
                if (odd) {
                    const value = objectValue(odd, 'matchOdds' + filterCriteria.selectionOdd + filterCriteria.priceOdd);
                    if (value === null) return false;
                    if (value < filterCriteria.minOddOdd || value > filterCriteria.maxOddOdd) return false;
                }
            } else {
                return false;
            }
            if (filterCriteria.selectionUnderOver) {
                const odd = objectValue(oddFilter, element.bfEventId + matchToPick);
                if (odd) {
                    const value = objectValue(odd, filterCriteria.selectionUnderOver.toLowerCase() + 'TwoPFive' + filterCriteria.priceUnderOver);
                    if (value === null) return false;
                    if (value < filterCriteria.minOddUnderOver || value > filterCriteria.maxOddUnderOver) return false;
                } else {
                    return false;
                }
            }
            if (filterCriteria.selectionCorrectScore) {
                const odd = objectValue(oddFilter, element.bfEventId + matchToPick);
                if (odd) {
                    const csMap = {
                        '0-0': 'ZeroZero', '0-1': 'ZeroOne', '0-2': 'ZeroTwo', '0-3': 'ZeroThree',
                        '1-0': 'OneZero', '1-1': 'OneOne', '1-2': 'OneTwo', '1-3': 'OneThree',
                        '2-0': 'TwoZero', '2-1': 'TwoOne', '2-2': 'TwoTwo', '2-3': 'TwoThree',
                        '3-0': 'ThreeZero', '3-1': 'ThreeOne', '3-2': 'ThreeTwo', '3-3': 'ThreeThree',
                        'Other Home Win': 'AoHw', 'Other Away Win': 'AoAw', 'Other Draw Win': 'AoDw'
                    };
                    const csKey = csMap[filterCriteria.selectionCorrectScore] || filterCriteria.selectionCorrectScore;
                    const value = objectValue(odd, 'ftCorrectScore' + csKey + filterCriteria.priceCorrectScore);
                    if (value === null) return false;
                    if (value < filterCriteria.minOddCorrectScore || value > filterCriteria.maxOddCorrectScore) return false;
                } else {
                    return false;
                }
            }
            if (filterCriteria.minBtts !== 0 || filterCriteria.maxBtts !== 100) {
                if (element.combBtts) {
                    const value = element.combBtts;
                    if (value !== null && (value < filterCriteria.minBtts || value > filterCriteria.maxBtts)) return false;
                } else {
                    return false;
                }
            }
            if (filterCriteria.minCS25 !== 0 || filterCriteria.maxCS25 !== 100) {
                if (element.combOverTwoPointFive) {
                    const value = element.combOverTwoPointFive;
                    if (value !== null && (value < filterCriteria.minCS25 || value > filterCriteria.maxCS25)) return false;
                } else {
                    return false;
                }
            }
            if (filterCriteria.minCombGS !== 0 || filterCriteria.maxCombGS !== 100) {
                if (element.combGoalsScoredConc) {
                    const value = element.combGoalsScoredConc;
                    if (value !== null && (value < filterCriteria.minCombGS || value > filterCriteria.maxCombGS)) return false;
                } else {
                    return false;
                }
            }
            if (filterCriteria.homeMin !== 0 || filterCriteria.homeMax !== 100) {
                if (element.rapidApiStat && element.rapidApiStat.home) {
                    const value = element.rapidApiStat.home.total;
                    if (value === -1 || value < filterCriteria.homeMin || value > filterCriteria.homeMax) return false;
                } else {
                    return false;
                }
            }
            if (filterCriteria.awayMin !== 0 || filterCriteria.awayMax !== 100) {
                if (element.rapidApiStat && element.rapidApiStat.away) {
                    const value = element.rapidApiStat.away.total;
                    if (value === -1 || value < filterCriteria.awayMin || value > filterCriteria.awayMax) return false;
                } else {
                    return false;
                }
            }
            return true;
        })

        return filtered.map((item, index) => (
            <League
                key={item.bfEventId || (item.bfHomeTeam + index)}
                date={props.selectionState.selectionDate}
                time={runner.raceTime}
                selectionState={props.selectionState}
                finalColumns={staticColumns}
                clickHandler={clickHandler}
                oddChangeHandler={oddChangeHandler}
                matchStatusChangeHandler={matchStatusChangeHandler}
                item={item}
                race={runner}
                index={index}
            />
        ))
    }, [
        props.soccerLeaguesState,
        props.selectionState.home,
        props.selectionState.away,
        props.selectionState.selectionDate,
        sortColumn,
        sortOrder,
        oddFilter,
        durationFilter,
        props.filterCriteria,
        clickHandler,
        oddChangeHandler,
        matchStatusChangeHandler,
        runner,
    ])

    let contentBody = null
    if (loadingState === 0 || loadingState === 1) {
        contentBody = <div style={{ textAlign: "center" }}><Spinner /></div>
    } else {
        contentBody = (
            <>
                <Table className="LeagueTable" style={{ border: "0px" }} id="runnerDetails" striped bordered hover size="sm">
                    <thead>
                        <tr>
                            {tableHeaders}
                        </tr>
                    </thead>
                    <tbody>
                        {itemList}
                    </tbody>
                </Table>
            </>
        )
    }

    return (
        <Container fluid className={classes.LeagueTable}>
            <Row>
                <Col>{contentBody}</Col>
            </Row>
        </Container>
    )
}

const mapStateToProps = state => {
    return {
        soccerLeaguesState: state.soccerLeaguesState,
        selectionState: state.selectionState,
        filterCriteria: state.filterCriteria,
    }
}

const mapDispatchToProps = dispatch => {
    return {
        setSoccerLeaguesState: payload =>
            dispatch({ type: SET_SOCCER_LEAGUES_STATE, payload: payload }),
        setSelectionState: payload =>
            dispatch({ type: SET_SELECTION_STATE, payload: payload }),
        setFilterCriteria: payload =>
            dispatch({ type: SET_FILTER, payload: payload }),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(LeagueTable);
